/**
 * RAG chat redux slice (native project chat over the KS /chat SSE stream).
 *
 * Same RTK-core shape as the QCE slice (createSlice + a thunk; read with
 * useSelector) — not RTK Query, for the react-redux v9 / RTK v1.9 reason
 * documented in qceSlice.ts. The streaming turn is driven by a thunk that
 * dispatches incremental reducer actions as SSE events arrive.
 */
import { useSelector, useDispatch } from "react-redux";
import { createSlice } from "@reduxjs/toolkit";
import type { AnyAction, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";

import type { RootState } from "../rootReducer";
import { searchProjectKeyword, searchProjectThematic } from "../../actions/projects";
import { renumberCitations } from "../../../Pages/Project/Tabs/ChatTab/citations";
import { streamAgentChat, agentService } from "../qce/agentService";
import type {
  AddExcerptsResult,
  AgentCitation,
  ExcerptProposal,
  ProposedExcerpt,
  ThemeCandidate,
} from "../qce/agentService";

import { agentStepLabel } from "./agentStepLabels";
import { loadPersistedChat } from "./chatPersistence";
import { getChatToken, getCitationMeta, streamChat } from "./chatService";
import type { Citation, CitationMeta } from "./chatService";

// Map a KS AgentCitation (scoped /agent/chat plane) onto the chat slice's
// Citation shape so the shared SidePanel/citation UI renders agent answers too.
function agentCitationToCitation(c: AgentCitation, i: number): Citation {
  return {
    index: c.index ?? i + 1,
    transcription: c.text ?? "",
    text: c.text ?? "",
    // Empty (never "Unknown") — display sites fall back to the story title.
    speaker: c.speaker || (c.isChapterSynopsis ? "Chapter Summary" : ""),
    interviewTitle: c.storyTitle ?? "",
    storyTitle: c.storyTitle ?? "",
    sectionTitle: c.chapterTitle ?? "",
    chapterTitle: c.chapterTitle ?? "",
    startTime: c.startTime,
    endTime: c.endTime,
    theirstoryId: c.storyId,
    storyId: c.storyId,
    score: c.score,
    isChapterSynopsis: Boolean(c.isChapterSynopsis),
  };
}

export interface ChatTurn {
  // Stable id per turn so highlight/scroll can be scoped to a Q&A pair across
  // re-renders (data-assistant-message-id), not just the array position.
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  // Transient status line shown while the assistant turn is streaming.
  status?: string;
  streaming?: boolean;

  // ----- Agent-chat (tool-use) extras (U4b), only set in agent mode -----
  /** Human-readable tool steps the orchestrator ran (status → tool_step). */
  toolSteps?: string[];
  /** Themes the agent proposed this turn; the user saves them one-by-one. */
  proposedThemes?: ThemeCandidate[];
  /** Names of proposed themes the user has already saved (button → "Saved"). */
  savedThemeNames?: string[];
  /** Name of a proposed theme whose save is in flight (button spinner). */
  savingThemeName?: string | null;
  /** Last save error for this turn, shown inline under the proposals. */
  themeSaveError?: string | null;
  /** add_excerpts_to_theme proposals; each is confirmed (or retried) on its own. */
  excerptProposals?: ExcerptProposalState[];
}

/** One "add these excerpts to <theme>" proposal and its confirm lifecycle. */
export interface ExcerptProposalState {
  id: string;
  themeId: string;
  themeName: string;
  excerpts: ProposedExcerpt[];
  /** KS's count of excerpts it will add (absent on older KS — derive from excerpts). */
  addable?: number;
  skipped?: number;
  status: "idle" | "adding" | "added" | "error";
  result?: AddExcerptsResult;
  error?: string;
}

// Which view (if any) the side panel shows.
export type SidePanelMode = "hidden" | "recording" | "transcript" | "search";

export type PanelSearchMode = "keyword" | "thematic";

// One "back" step: the mode to return to and, for "transcript", which recording.
export interface PanelBackEntry {
  mode: SidePanelMode;
  transcriptCitation: Citation | null;
}

// A project-wide search run from a text selection (shared popover), shown in the
// panel's "search" mode. requestId drops responses from superseded searches.
export interface PanelSearchState {
  requestId: string;
  query: string;
  mode: PanelSearchMode;
  status: "loading" | "done" | "error";
  results: Citation[];
  // More hits exist than the first page shown.
  hasMore: boolean;
  error?: string;
}

interface TokenCache {
  token: string;
  chatUrl: string;
  projectId: string;
  // Epoch ms when the token was minted; re-mint before the 10m server expiry.
  mintedAt: number;
}

export interface ChatState {
  projectId: string | null;
  turns: ChatTurn[];
  sending: boolean;
  error: string | null;
  tokenCache: TokenCache | null;
  // KS-assigned thread id (from the `conversation` event); echoed on later turns
  // for server-side history + prompt-cache reuse.
  conversationId: string | null;

  // ----- Side panel (portals parity) -----
  sidePanelMode: SidePanelMode;
  // The citation whose clip/quote the recording view is focused on.
  activeCitation: Citation | null;
  // All citations for the answer the active citation belongs to (the "all
  // sources" list + sibling navigation).
  activeCitationSiblings: Citation[];
  // Scopes chip highlight/scroll to the answer the panel is showing.
  activeAssistantMessageId: string | null;
  // The citation the transcript view is open for.
  transcriptCitation: Citation | null;
  // Modes to return to on "back" (results → recording → back), most recent last.
  panelBackStack: PanelBackEntry[];
  panelSearch: PanelSearchState | null;
  // Cross-highlighting between a chip and its source row.
  hoveredCitationIndex: number | null;
  hoveredFromPanel: boolean;
  // Bumped to ask the thread to scroll the matching chip into view.
  scrollToCitationIndex: number | null;
}

// Clean slate — what "Clear chat" resets to. Distinct from the hydrated initial
// state so resetChat doesn't restore the persisted conversation.
const DEFAULT_STATE: ChatState = {
  projectId: null,
  turns: [],
  sending: false,
  error: null,
  tokenCache: null,
  conversationId: null,
  sidePanelMode: "hidden",
  activeCitation: null,
  activeCitationSiblings: [],
  activeAssistantMessageId: null,
  transcriptCitation: null,
  panelBackStack: [],
  panelSearch: null,
  hoveredCitationIndex: null,
  hoveredFromPanel: false,
  scrollToCitationIndex: null,
};

function getInitialProjectId(): string | null {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/\/projects\/([^/]+)/);
  return match ? match[1] : null;
}

// Rehydrate the saved conversation for the current project from localStorage so
// the chat survives reload.
const initialProjectId = getInitialProjectId();
const persisted = initialProjectId ? loadPersistedChat(initialProjectId) : null;
const initialState: ChatState =
  persisted && initialProjectId
    ? {
        ...DEFAULT_STATE,
        projectId: initialProjectId,
        turns: persisted.turns as ChatTurn[],
        conversationId: persisted.conversationId,
      }
    : { ...DEFAULT_STATE, projectId: initialProjectId };

// The agent re-emits its full, growing citation set after every tool round; keep
// the CMS display metadata already merged for a story so a re-emit never drops
// thumbnails/titles back to placeholders while its own enrichment is in flight.
function carryCitationMeta(prev: Citation[] | undefined, next: Citation[]): Citation[] {
  if (!prev?.length) return next;
  const byStory = new Map(prev.filter((c) => c.thumbnailUrl || c.isAudioFile != null).map((c) => [c.theirstoryId, c]));
  return next.map((c) => {
    const known = byStory.get(c.theirstoryId);
    if (!known || c.thumbnailUrl || c.isAudioFile != null) return c;
    return {
      ...c,
      interviewTitle: known.interviewTitle || c.interviewTitle,
      thumbnailUrl: known.thumbnailUrl,
      isAudioFile: known.isAudioFile,
    };
  });
}

// Merge CMS display metadata (title, thumbnail, audio flag) into citations by story.
// Stories missing from `meta` are marked resolved too (isAudioFile set), so their
// thumbnail skeleton settles to the plain placeholder.
function applyCitationMeta(citations: Citation[], meta: Record<string, CitationMeta>): Citation[] {
  return citations.map((c) => {
    const m = meta[c.theirstoryId];
    if (!m) return c.isAudioFile === undefined ? { ...c, isAudioFile: false } : c;
    return {
      ...c,
      interviewTitle: m.title || c.interviewTitle,
      thumbnailUrl: m.thumbnailUrl ?? c.thumbnailUrl,
      isAudioFile: m.isAudioFile,
    };
  });
}

// Navigating from `current` to `next` remembers `current` (and the recording it
// showed) for "back"; the panel being hidden, or re-entering the same mode, adds nothing.
export function pushPanelMode(
  stack: PanelBackEntry[],
  current: SidePanelMode,
  next: SidePanelMode,
  transcriptCitation: Citation | null
): PanelBackEntry[] {
  if (current === "hidden" || current === next) return stack;
  return [...stack, { mode: current, transcriptCitation }];
}

// Find the assistant turn a citations array belongs to (by identity of the
// array reference, falling back to matching the last assistant turn).
function findMessageIdForCitations(state: ChatState, citations: Citation[]): string | null {
  const turn = state.turns.find((t) => t.role === "assistant" && t.citations === citations);
  return turn?.id ?? null;
}

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addUserTurn(state, { payload }: PayloadAction<string>) {
      state.turns.push({ id: nanoid(), role: "user", content: payload });
      // Placeholder assistant turn that the stream fills in.
      state.turns.push({ id: nanoid(), role: "assistant", content: "", streaming: true, status: "Thinking…" });
      state.sending = true;
      state.error = null;
    },
    setStatus(state, { payload }: PayloadAction<string>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant") t.status = payload;
    },
    setCitations(state, { payload }: PayloadAction<Citation[]>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant") t.citations = carryCitationMeta(t.citations, payload);
    },
    appendDelta(state, { payload }: PayloadAction<string>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant") {
        t.content += payload;
        t.status = undefined; // first token clears the status line
      }
    },
    finishTurn(state) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant") {
        t.streaming = false;
        t.status = undefined;
        // Renumber references to 1..k in order of appearance and drop uncited
        // sources, so the visible markers and the sources list line up.
        if (t.content && t.citations?.length) {
          const { content, citations } = renumberCitations(t.content, t.citations);
          t.content = content;
          t.citations = citations;
        }
        // Auto-open the side panel to the first cited source.
        if (t.citations?.length) {
          state.activeCitation = t.citations[0];
          state.activeCitationSiblings = t.citations;
          state.activeAssistantMessageId = t.id;
          state.sidePanelMode = "recording";
          state.panelBackStack = [];
        }
      }
      state.sending = false;
    },
    failTurn(state, { payload }: PayloadAction<string>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant") {
        t.streaming = false;
        t.status = undefined;
        if (!t.content) t.content = "Sorry — something went wrong answering that.";
      }
      state.sending = false;
      state.error = payload;
    },
    setTokenCache(state, { payload }: PayloadAction<TokenCache>) {
      state.tokenCache = payload;
    },
    setConversationId(state, { payload }: PayloadAction<string>) {
      if (payload) state.conversationId = payload;
    },

    // ----- Agent-chat (tool-use) actions (U4b) -----
    addToolStep(state, { payload }: PayloadAction<string>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role === "assistant" && payload) {
        t.toolSteps = [...(t.toolSteps || []), payload];
      }
    },
    setProposedThemes(state, { payload }: PayloadAction<ThemeCandidate[]>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role !== "assistant") return;
      // Accumulate + dedup by name: generate_themes delivers a batch at once, while
      // create_theme proposals trickle in one tool_step at a time (e.g. "save 3, 5
      // and 6" → three drafts). Both must all show their Save CTA.
      const existing = t.proposedThemes ?? [];
      const names = new Set(existing.map((c) => c.name));
      t.proposedThemes = [...existing, ...payload.filter((c) => !names.has(c.name))];
    },
    addExcerptProposal(state, { payload }: PayloadAction<ExcerptProposal>) {
      const t = state.turns[state.turns.length - 1];
      if (t?.role !== "assistant") return;
      t.excerptProposals = [
        ...(t.excerptProposals ?? []),
        {
          id: nanoid(),
          themeId: payload.target.themeId,
          themeName: payload.target.themeName,
          excerpts: payload.excerpts,
          addable: payload.addable,
          skipped: payload.skipped,
          status: "idle",
        },
      ];
    },
    updateExcerptProposal(
      state,
      {
        payload,
      }: PayloadAction<
        { turnId: string; id: string } & Partial<Pick<ExcerptProposalState, "status" | "result" | "error">>
      >
    ) {
      const { turnId, id, ...patch } = payload;
      const p = state.turns.find((x) => x.id === turnId)?.excerptProposals?.find((x) => x.id === id);
      if (p) Object.assign(p, patch);
    },
    startSaveTheme(state, { payload }: PayloadAction<{ turnId: string; name: string }>) {
      const t = state.turns.find((x) => x.id === payload.turnId);
      if (t) {
        t.savingThemeName = payload.name;
        t.themeSaveError = null;
      }
    },
    themeSaved(state, { payload }: PayloadAction<{ turnId: string; name: string }>) {
      const t = state.turns.find((x) => x.id === payload.turnId);
      if (t) {
        t.savedThemeNames = [...(t.savedThemeNames || []), payload.name];
        if (t.savingThemeName === payload.name) t.savingThemeName = null;
      }
    },
    themeSaveFailed(state, { payload }: PayloadAction<{ turnId: string; message: string }>) {
      const t = state.turns.find((x) => x.id === payload.turnId);
      if (t) {
        t.savingThemeName = null;
        t.themeSaveError = payload.message;
      }
    },

    // ----- Side panel actions -----
    // Chip click: open that recording directly (seeked to the citation); "Back to
    // sources" returns to this answer's sources list.
    setActiveCitation(state, { payload }: PayloadAction<{ citation: Citation; siblings: Citation[] }>) {
      state.activeCitation = payload.citation;
      state.activeCitationSiblings = payload.siblings;
      state.activeAssistantMessageId = findMessageIdForCitations(state, payload.siblings);
      state.panelBackStack = [{ mode: "recording", transcriptCitation: state.transcriptCitation }];
      state.transcriptCitation = payload.citation;
      state.sidePanelMode = "transcript";
    },
    showSourcesForMessage(state, { payload }: PayloadAction<string>) {
      const turn = state.turns.find((t) => t.id === payload && t.role === "assistant");
      if (!turn?.citations?.length) return;
      state.activeCitation = null;
      state.activeCitationSiblings = turn.citations;
      state.activeAssistantMessageId = turn.id;
      state.sidePanelMode = "recording";
      state.panelBackStack = [];
    },
    openTranscript(state, { payload }: PayloadAction<Citation>) {
      state.panelBackStack = pushPanelMode(
        state.panelBackStack,
        state.sidePanelMode,
        "transcript",
        state.transcriptCitation
      );
      state.transcriptCitation = payload;
      state.sidePanelMode = "transcript";
    },
    goBack(state) {
      const entry = state.panelBackStack.pop();
      state.sidePanelMode = entry?.mode ?? "recording";
      if (entry?.mode === "transcript" && entry.transcriptCitation) {
        state.transcriptCitation = entry.transcriptCitation;
      }
    },
    closeSidePanel(state) {
      state.sidePanelMode = "hidden";
      state.activeCitation = null;
      state.transcriptCitation = null;
      state.panelBackStack = [];
      state.panelSearch = null;
    },
    startPanelSearch(state, { payload }: PayloadAction<{ requestId: string; query: string; mode: PanelSearchMode }>) {
      state.panelBackStack = pushPanelMode(
        state.panelBackStack,
        state.sidePanelMode,
        "search",
        state.transcriptCitation
      );
      state.sidePanelMode = "search";
      state.panelSearch = { ...payload, status: "loading", results: [], hasMore: false };
    },
    panelSearchLoaded(state, { payload }: PayloadAction<{ requestId: string; results: Citation[]; hasMore: boolean }>) {
      if (state.panelSearch?.requestId !== payload.requestId) return;
      state.panelSearch.status = "done";
      state.panelSearch.results = payload.results;
      state.panelSearch.hasMore = payload.hasMore;
    },
    panelSearchFailed(state, { payload }: PayloadAction<{ requestId: string; error: string }>) {
      if (state.panelSearch?.requestId !== payload.requestId) return;
      state.panelSearch.status = "error";
      state.panelSearch.error = payload.error;
    },
    mergePanelSearchMeta(state, { payload }: PayloadAction<{ requestId: string; meta: Record<string, CitationMeta> }>) {
      if (state.panelSearch?.requestId !== payload.requestId) return;
      state.panelSearch.results = applyCitationMeta(state.panelSearch.results, payload.meta);
    },
    setHoveredCitationIndex(state, { payload }: PayloadAction<{ index: number | null; fromPanel?: boolean }>) {
      state.hoveredCitationIndex = payload.index;
      state.hoveredFromPanel = payload.fromPanel ?? false;
    },
    scrollToCitation(state, { payload }: PayloadAction<number>) {
      state.scrollToCitationIndex = payload;
    },
    clearScrollToCitation(state) {
      state.scrollToCitationIndex = null;
    },

    // Merge CMS-owned display metadata (title, thumbnail, audio flag) into a
    // specific turn's citations — matched by turn id so a later query can't
    // corrupt an earlier turn's sources. Keeps the open panel in sync.
    mergeCitationMeta(state, { payload }: PayloadAction<{ turnId: string; meta: Record<string, CitationMeta> }>) {
      const turn = state.turns.find((t) => t.id === payload.turnId);
      if (!turn?.citations) return;
      turn.citations = applyCitationMeta(turn.citations, payload.meta);
      if (state.activeAssistantMessageId === turn.id) {
        state.activeCitationSiblings = turn.citations;
        if (state.activeCitation) {
          const idx = state.activeCitation.index;
          const found = turn.citations.find((c) => c.index === idx);
          if (found) state.activeCitation = found;
        }
      }
    },

    initProjectChat(state, { payload }: PayloadAction<string>) {
      if (state.projectId === payload) return;
      const savedChat = loadPersistedChat(payload);
      Object.assign(state, DEFAULT_STATE, {
        projectId: payload,
        turns: (savedChat?.turns as ChatTurn[]) ?? [],
        conversationId: savedChat?.conversationId ?? null,
      });
    },

    resetChat(state, { payload }: PayloadAction<string | undefined>) {
      // Clear to a clean slate for the project — the middleware wipes
      // localStorage on this action.
      const targetProjectId = payload || state.projectId;
      Object.assign(state, DEFAULT_STATE, {
        projectId: targetProjectId,
      });
    },
  },
});

export const {
  initProjectChat,
  addUserTurn,
  setStatus,
  setCitations,
  appendDelta,
  finishTurn,
  failTurn,
  setTokenCache,
  setConversationId,
  addToolStep,
  setProposedThemes,
  addExcerptProposal,
  updateExcerptProposal,
  startSaveTheme,
  themeSaved,
  themeSaveFailed,
  setActiveCitation,
  showSourcesForMessage,
  openTranscript,
  goBack,
  closeSidePanel,
  startPanelSearch,
  panelSearchLoaded,
  panelSearchFailed,
  mergePanelSearchMeta,
  mergeCitationMeta,
  setHoveredCitationIndex,
  scrollToCitation,
  clearScrollToCitation,
  resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;

type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

// Fetch CMS-owned display metadata (title, thumbnail, audio) for the cited stories
// and merge it into that turn (by id, never a later one). Shared by the RAG and
// agent thunks so both modes enrich identically. Fire-and-forget: the answer
// renders regardless; titles/thumbnails fill in when it resolves.
function enrichCitations(dispatch: AppDispatch, projectId: string, turnId: string | undefined, citations: Citation[]) {
  const storyIds = Array.from(new Set(citations.map((c) => c.theirstoryId).filter(Boolean)));
  if (storyIds.length === 0 || !turnId) return;
  getCitationMeta(projectId, storyIds)
    .catch(() => ({}))
    .then((meta) => dispatch(mergeCitationMeta({ turnId, meta })));
}

// A rag_api token is good for ~10m; re-mint a minute early to avoid mid-turn expiry.
const TOKEN_TTL_MS = 9 * 60 * 1000;

// Send one chat turn: ensure a fresh token, then stream the answer, dispatching
// incremental updates as events arrive.
export const sendChatMessage =
  (args: { orgId: string; projectId: string; message: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { orgId, projectId, message } = args;

    if (getState().chat.projectId !== projectId) {
      dispatch(initProjectChat(projectId));
    }

    // History is the conversation *before* this turn (exclude the just-added placeholder).
    const priorTurns = getState().chat.turns.filter((t) => !t.streaming);
    const history = priorTurns.map((t) => ({ role: t.role, content: t.content }));

    dispatch(addUserTurn(message));
    // The assistant turn just pushed — capture its id so async citation
    // enrichment merges into THIS turn, never a later one.
    const turns = getState().chat.turns;
    const assistantTurnId = turns[turns.length - 1]?.id;

    try {
      let cache = getState().chat.tokenCache;
      const fresh = cache && cache.projectId === projectId && Date.now() - cache.mintedAt < TOKEN_TTL_MS;
      if (!fresh) {
        const { token, chatUrl } = await getChatToken(orgId, projectId);
        cache = { token, chatUrl, projectId, mintedAt: Date.now() };
        dispatch(setTokenCache(cache));
      }

      if (!cache) {
        throw new Error("Failed to obtain chat token");
      }

      const conversationId = getState().chat.conversationId || undefined;

      await streamChat(
        { chatUrl: cache.chatUrl, token: cache.token, message, history, orgId, projectId, conversationId },
        (e) => {
          if (e.type === "conversation") dispatch(setConversationId(e.conversationId));
          else if (e.type === "status") dispatch(setStatus(e.message));
          else if (e.type === "citations") {
            dispatch(setCitations(e.citations));
            enrichCitations(dispatch, projectId, assistantTurnId, e.citations);
          } else if (e.type === "text") dispatch(appendDelta(e.delta));
          else if (e.type === "done") dispatch(finishTurn());
          else if (e.type === "error") dispatch(failTurn(e.message));
        }
      );
      // Stream closed without an explicit done event — finalize anyway.
      if (getState().chat.sending) dispatch(finishTurn());
    } catch (err: any) {
      dispatch(failTurn(err?.message || "Chat request failed"));
    }
  };

// Send one turn through the *tool-use* orchestrator (`POST /agent/chat`, U4b).
// Same per-project scope as sendChatMessage (streamAgentChat mints the scoped
// rag_api token itself), but surfaces tool steps and proposed themes; the writes
// only happen when the user confirms a proposal (confirmCreateTheme). There is
// no org-wide chat — orgId+projectId scope every call, exactly as the RAG chat.
export const sendAgentChatMessage =
  (args: { orgId: string; projectId: string; message: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { orgId, projectId, message } = args;

    if (getState().chat.projectId !== projectId) {
      dispatch(initProjectChat(projectId));
    }

    dispatch(addUserTurn(message));
    const turns = getState().chat.turns;
    const assistantTurnId = turns[turns.length - 1]?.id;

    // Thread the conversation by id (server keeps history + prompt cache); no
    // need to resend the full transcript. Mirrors the RAG chat thunk.
    const conversationId = getState().chat.conversationId || undefined;

    try {
      await streamAgentChat(
        orgId,
        projectId,
        message,
        (e) => {
          switch (e.type) {
            case "conversation":
              dispatch(setConversationId(e.conversationId));
              break;
            case "status":
              dispatch(setStatus(e.message));
              break;
            case "tool_step":
              // Friendly, one-line present-tense label — never the raw tool name.
              dispatch(addToolStep(agentStepLabel(e.tool, e.status, e.summary)));
              break;
            case "citations": {
              const mapped = e.citations.map(agentCitationToCitation);
              dispatch(setCitations(mapped));
              enrichCitations(dispatch, projectId, assistantTurnId, mapped);
              break;
            }
            case "candidates":
              dispatch(setProposedThemes(e.candidates));
              break;
            case "excerpt_proposal":
              dispatch(addExcerptProposal(e.proposal));
              break;
            case "text":
              dispatch(appendDelta(e.delta));
              break;
            case "done":
              dispatch(finishTurn());
              break;
            case "error":
              dispatch(failTurn(e.message));
              break;
            default:
              break;
          }
        },
        undefined,
        conversationId
      );
      if (getState().chat.sending) dispatch(finishTurn());
    } catch (err: any) {
      dispatch(failTurn(err?.message || "Agent chat failed"));
    }
  };

// Persist a proposed theme on explicit user confirmation (U4b). Two-step by
// design: the agent only *proposed* it; this fires the guarded create_theme
// write with confirm:true when the user clicks save.
export const confirmCreateTheme =
  (args: { orgId: string; projectId: string; turnId: string; candidate: ThemeCandidate }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const { orgId, projectId, turnId, candidate } = args;
    dispatch(startSaveTheme({ turnId, name: candidate.name }));
    try {
      await agentService.createTheme(orgId, projectId, {
        name: candidate.name,
        description: candidate.description,
        keywords: candidate.keywords,
        exemplars: candidate.exemplars,
        skill: candidate.skill,
        confirm: true,
      });
      dispatch(themeSaved({ turnId, name: candidate.name }));
    } catch (err: any) {
      dispatch(themeSaveFailed({ turnId, message: err?.message || "Could not save this theme" }));
    }
  };

// Add a proposed set of excerpts to a theme on explicit user confirmation. Like
// confirmCreateTheme: the agent only *proposed* them; this fires the guarded
// add_excerpts_to_theme write with confirm:true. Resolves true on success.
export const confirmAddExcerpts =
  (args: { orgId: string; projectId: string; turnId: string; proposalId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const { orgId, projectId, turnId, proposalId } = args;
    const proposal = getState()
      .chat.turns.find((t) => t.id === turnId)
      ?.excerptProposals?.find((p) => p.id === proposalId);
    if (!proposal) return false;
    dispatch(updateExcerptProposal({ turnId, id: proposalId, status: "adding", error: undefined }));
    try {
      const result = await agentService.addExcerptsToTheme(orgId, projectId, {
        themeId: proposal.themeId,
        excerpts: proposal.excerpts,
        confirm: true,
      });
      dispatch(updateExcerptProposal({ turnId, id: proposalId, status: "added", result }));
      return true;
    } catch (err: any) {
      dispatch(
        updateExcerptProposal({
          turnId,
          id: proposalId,
          status: "error",
          error: err?.message || "Could not add these excerpts",
        })
      );
      return false;
    }
  };

export const PANEL_SEARCH_PAGE_SIZE = 50;

// A project search hit (story + matched chunk) in the panel's Citation shape, so
// the Sources views (grouping, thumbnails) render results as-is.
function searchHitToCitation(hit: any, i: number): Citation {
  const start = typeof hit.start_time === "number" ? hit.start_time : 0;
  const end = typeof hit.end_time === "number" && hit.end_time > start ? hit.end_time : start;
  const text = hit.highlight || hit.transcription || hit.chunk_text || "";
  return {
    index: i + 1,
    transcription: text,
    text,
    speaker: hit.speaker || "",
    interviewTitle: hit.title || "",
    storyTitle: hit.title || "",
    sectionTitle: Array.isArray(hit.chapters) ? hit.chapters.join(", ") : "",
    startTime: start,
    endTime: end,
    theirstoryId: String(hit._id),
    storyId: String(hit._id),
    score: hit.score,
  };
}

// Search the chat's project for selected text — exactly the Sessions/Hub project
// search (Keyword = v1, Find similar = thematic v2) — into the panel's "search" mode.
export const runPanelSearch =
  (args: { query: string; mode: PanelSearchMode }) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { projectId } = getState().chat;
    const query = args.query.trim();
    if (!projectId || !query) return;
    const requestId = nanoid();
    dispatch(startPanelSearch({ requestId, query, mode: args.mode }));
    try {
      const { items, total } =
        args.mode === "thematic"
          ? await searchProjectThematic(projectId, query, { pageSize: PANEL_SEARCH_PAGE_SIZE })
          : await searchProjectKeyword(projectId, query, { page: 1, pageSize: PANEL_SEARCH_PAGE_SIZE });
      const results = (items ?? []).map(searchHitToCitation);
      const storyIds = Array.from(new Set(results.map((c) => c.theirstoryId)));
      // `total` counts hits for thematic but recordings for keyword (one page of
      // stories, each with its matches as rows).
      const hasMore =
        args.mode === "thematic"
          ? (total ?? 0) > results.length
          : (total ?? 0) > storyIds.length || storyIds.length >= PANEL_SEARCH_PAGE_SIZE;
      dispatch(panelSearchLoaded({ requestId, results, hasMore }));
      if (storyIds.length) {
        getCitationMeta(projectId, storyIds)
          .catch(() => ({}))
          .then((meta) => dispatch(mergePanelSearchMeta({ requestId, meta })));
      }
    } catch (err: any) {
      dispatch(panelSearchFailed({ requestId, error: err?.message || "Search failed" }));
    }
  };

// ----- Hooks -----
export const useChatDispatch = () => useDispatch<AppDispatch>();
export const useChat = () => useSelector((s: RootState) => s.chat);
